import { useState, useEffect } from 'react';
import { CheckCircle, Plus, Filter, Search, Eye, Edit, Clock, User, AlertCircle, X } from 'lucide-react';
import { HeroSection } from '../HeroSection';
import { projectDecisionsService } from '../../services/projectDecisions';
import { OwnerSelect } from '../common/OwnerSelect';
import { usersService, type User as OrgUser } from '../../services/users';
import { toast } from 'sonner';
import type { ApiErrorLike } from '../../utils/apiError'

interface Decision {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  requestedBy: string;
  decisionMaker: string;
  dateRequested: string;
  dateDecided: string | null;
  dueDate: string;
  impact: string;
  rationale: string;
  alternatives: string;
  stakeholders: string[];
}

function formatDate(d: string | null): string {
  if (!d) return '';
  const x = d.slice(0, 10);
  return x;
}

function mapApiToDecision(item: import('../../services/projectDecisions').ProjectDecisionItem): Decision {
  return {
    id: `D-${String(item.id).padStart(3, '0')}`,
    title: item.title,
    description: item.description || '',
    category: item.category || '',
    status: item.status || 'Pending',
    priority: item.priority || 'Medium',
    requestedBy: item.requested_by || '',
    decisionMaker: item.decision_maker || '',
    dateRequested: formatDate(item.date_requested),
    dateDecided: item.date_decided ? formatDate(item.date_decided) : null,
    dueDate: formatDate(item.due_date) || '',
    impact: item.impact || '',
    rationale: item.rationale || '',
    alternatives: item.alternatives || '',
    stakeholders: item.stakeholders || [],
  };
}

interface DecisionsSectionProps {
  projectId?: number;
}

export function DecisionsSection({ projectId }: DecisionsSectionProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadDecisions = () => {
    if (!projectId) {
      setDecisions([]);
      return;
    }
    projectDecisionsService
      .list(projectId)
      .then((list) => setDecisions(list.map(mapApiToDecision)))
      .catch(() => {
        toast.error('Failed to load decisions');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setDecisions([]);
      });
  };

  useEffect(() => {
    loadDecisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Sprint 19 — real create flow. Backend POST has existed since Part 9
  // but the UI never wired it; Sprint 17 had to hide the dead button.
  const handleCreateDecision = async (data: DecisionFormValues) => {
    if (!projectId) {
      toast.error('Save the project before adding decisions');
      return;
    }
    setSaving(true);
    try {
      await projectDecisionsService.create(projectId, {
        title: data.title,
        description: data.description || null,
        category: data.category || null,
        // Status / dateRequested are set at creation time so the row
        // shows up in the right bucket on the dashboard immediately.
        status: 'Pending',
        priority: data.priority,
        requested_by: data.requested_by || null,
        decision_maker: data.decision_maker || null,
        date_requested: new Date().toISOString().slice(0, 10),
        due_date: data.due_date || null,
        impact: data.impact || null,
        rationale: data.rationale || null,
        alternatives: data.alternatives || null,
        stakeholders: data.stakeholders.length ? data.stakeholders : null,
      });
      toast.success('Decision added');
      setShowForm(false);
      loadDecisions();
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add decision');
    } finally {
      setSaving(false);
    }
  };

  const decisionStats = {
    total: decisions.length,
    approved: decisions.filter(d => d.status === 'Approved').length,
    pending: decisions.filter(d => d.status === 'Pending').length,
    deferred: decisions.filter(d => d.status === 'Deferred').length,
    rejected: decisions.filter(d => d.status === 'Rejected').length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved': return 'bg-green-100 text-green-700';
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Deferred': return 'bg-gray-100 text-gray-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'border-l-red-500';
      case 'High': return 'border-l-orange-500';
      case 'Medium': return 'border-l-yellow-500';
      default: return 'border-l-gray-300';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Hero Section */}
      <HeroSection
        title="Decision Log"
        description="Track key project decisions and approvals"
        variant="level4"
        metrics={[
          { label: 'Total Decisions', value: decisionStats.total.toString(), icon: CheckCircle, iconColor: 'bg-blue-50' },
          { label: 'Approved', value: decisionStats.approved.toString(), icon: CheckCircle, iconColor: 'bg-green-50' },
          { label: 'Pending', value: decisionStats.pending.toString(), icon: Clock, iconColor: 'bg-amber-50' },
          { label: 'Deferred', value: decisionStats.deferred.toString(), icon: AlertCircle, iconColor: 'bg-gray-50' },
        ]}
        actionButton={projectId ? {
          label: 'Request Decision',
          onClick: () => setShowForm(true),
          icon: <Plus className="w-4 h-4" />,
        } : undefined}
      />

      {showForm && (
        <DecisionForm
          onClose={() => setShowForm(false)}
          onSave={handleCreateDecision}
          saving={saving}
        />
      )}

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search decisions by title, ID, or decision maker..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Decision List */}
      <div className="space-y-4">
        {decisions.map((decision) => (
          <div 
            key={decision.id} 
            className={`bg-white rounded-lg border-l-4 border-r border-t border-b border-gray-200 hover:shadow-md transition-shadow ${getPriorityColor(decision.priority)}`}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono text-sm font-medium text-gray-700">{decision.id}</span>
                    <h3 className="text-lg font-medium">{decision.title}</h3>
                    <span className={`px-3 py-1 rounded text-sm ${getStatusColor(decision.status)}`}>
                      {decision.status}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-3">{decision.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {decision.category}
                    </span>
                    <span className="text-gray-600">
                      Priority: <span className="font-medium">{decision.priority}</span>
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Eye className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Edit className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Key Information Grid */}
              <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <User className="w-3 h-3" />
                    Requested By
                  </div>
                  <div className="text-sm font-medium">{decision.requestedBy}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <CheckCircle className="w-3 h-3" />
                    Decision Maker
                  </div>
                  <div className="text-sm font-medium">{decision.decisionMaker}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <Clock className="w-3 h-3" />
                    Date Requested
                  </div>
                  <div className="text-sm">{decision.dateRequested}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                    <Clock className="w-3 h-3" />
                    {decision.dateDecided ? 'Date Decided' : 'Due Date'}
                  </div>
                  <div className={`text-sm ${
                    !decision.dateDecided && new Date(decision.dueDate) < new Date() ? 'text-red-600 font-medium' : ''
                  }`}>
                    {decision.dateDecided || decision.dueDate}
                  </div>
                </div>
              </div>

              {/* Decision Details */}
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-xs text-blue-700 font-medium mb-1">Impact</div>
                  <div className="text-sm text-gray-700">{decision.impact}</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-xs text-green-700 font-medium mb-1">Rationale</div>
                  <div className="text-sm text-gray-700">{decision.rationale}</div>
                </div>
                {decision.alternatives && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="text-xs text-purple-700 font-medium mb-1">Alternatives Considered</div>
                    <div className="text-sm text-gray-700">{decision.alternatives}</div>
                  </div>
                )}
              </div>

              {/* Stakeholders */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs text-gray-600 mb-2">Key Stakeholders</div>
                <div className="flex flex-wrap gap-2">
                  {(decision.stakeholders || []).map((stakeholder, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm">
                      {stakeholder}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sprint 19 — Decision create modal
//
// Mirrors the inline RiskForm pattern in RisksSection.tsx so behaviour
// stays consistent across RAID surfaces. Stakeholders is a multi-pick:
// a single OwnerSelect-style dropdown that appends names into a list
// the user can chip-remove.
// ──────────────────────────────────────────────────────────────────────

const CATEGORY_OPTIONS = ['Technical', 'Financial', 'Schedule', 'Strategy', 'Resource', 'Compliance', 'Other'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

interface DecisionFormValues {
  title: string;
  description: string;
  category: string;
  priority: string;
  requested_by: string;
  decision_maker: string;
  due_date: string;
  impact: string;
  rationale: string;
  alternatives: string;
  stakeholders: string[];
}

function DecisionForm({
  onClose,
  onSave,
  saving,
}: {
  onClose: () => void;
  onSave: (data: DecisionFormValues) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<DecisionFormValues>({
    title: '',
    description: '',
    category: 'Technical',
    priority: 'Medium',
    requested_by: '',
    decision_maker: '',
    due_date: '',
    impact: '',
    rationale: '',
    alternatives: '',
    stakeholders: [],
  });

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [stakeholderDraft, setStakeholderDraft] = useState('');

  useEffect(() => {
    // Stakeholder picker shares the assignable-users endpoint with
    // OwnerSelect — same module cache pays off here.
    usersService
      .assignable({ limit: 200 })
      .then(setUsers)
      .catch(() => {
        // Non-critical — picker degrades to "no options" but the
        // user can still type a free-text name if we add that later.
        console.warn('DecisionForm: failed to load assignable users');
      });
  }, []);

  const userOptions = users.map((u) => (u.full_name && u.full_name.trim()) || u.email);
  const availableStakeholders = userOptions.filter((n) => !form.stakeholders.includes(n));

  const update = <K extends keyof DecisionFormValues>(key: K, value: DecisionFormValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addStakeholder = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || form.stakeholders.includes(trimmed)) return;
    update('stakeholders', [...form.stakeholders, trimmed]);
    setStakeholderDraft('');
  };

  const removeStakeholder = (name: string) => {
    update(
      'stakeholders',
      form.stakeholders.filter((n) => n !== name),
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl">Request a Decision</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm mb-2">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
              required
              autoFocus
              placeholder="Short decision title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              placeholder="What needs to be decided?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Category</label>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => update('priority', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Requested By</label>
              <OwnerSelect
                value={form.requested_by}
                onChange={(v) => update('requested_by', v)}
                placeholder="Pick a requester"
              />
            </div>
            <div>
              <label className="block text-sm mb-2">Decision Maker</label>
              <OwnerSelect
                value={form.decision_maker}
                onChange={(v) => update('decision_maker', v)}
                placeholder="Pick a decision maker"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-2">Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => update('due_date', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Stakeholders</label>
            <div className="flex gap-2">
              <select
                value={stakeholderDraft}
                onChange={(e) => setStakeholderDraft(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Add a stakeholder…</option>
                {availableStakeholders.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <button
                type="button"
                onClick={() => addStakeholder(stakeholderDraft)}
                disabled={!stakeholderDraft}
                className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
            {form.stakeholders.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.stakeholders.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-sm text-blue-700"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeStakeholder(name)}
                      className="hover:text-blue-900"
                      aria-label={`Remove ${name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm mb-2">Impact</label>
            <textarea
              value={form.impact}
              onChange={(e) => update('impact', e.target.value)}
              rows={2}
              placeholder="What does this decision affect?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Rationale</label>
            <textarea
              value={form.rationale}
              onChange={(e) => update('rationale', e.target.value)}
              rows={2}
              placeholder="Why this approach?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Alternatives Considered</label>
            <textarea
              value={form.alternatives}
              onChange={(e) => update('alternatives', e.target.value)}
              rows={2}
              placeholder="What other options were on the table?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Add Decision'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}